import { useEffect } from "react";
import type { Video } from "@/types";

interface PlayerModalProps {
  video: Video;
  open: boolean;
  onClose: () => void;
}

export default function PlayerModal({ video, open, onClose }: PlayerModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-primary-950/85 animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl rounded-lg overflow-hidden bg-primary-950 border border-primary-800 animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-primary-800">
          <div className="flex items-center gap-2 min-w-0">
            <i className="ri-play-circle-line text-secondary-400 text-lg shrink-0" />
            <h3 className="text-sm font-semibold text-background-50 truncate">{video.title}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="关闭播放器"
            className="flex items-center justify-center w-8 h-8 rounded-md text-background-300 hover:text-background-50 hover:bg-primary-800 cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="relative w-full aspect-video bg-primary-900">
          <img
            src={video.cover}
            alt={`${video.title} 播放预览`}
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
            <span className="flex items-center justify-center w-16 h-16 rounded-full bg-secondary-500 text-background-50 mb-4">
              <i className="ri-play-fill text-3xl" />
            </span>
            <p className="text-sm md:text-base text-background-100 font-medium">演示播放器</p>
            <p className="mt-1 text-xs md:text-sm text-background-300 max-w-md">
              当前为前端演示占位区域，真实视频播放需接入后端播放接口后启用。
            </p>
          </div>
          <div className="absolute bottom-0 left-0 right-0 px-4 pb-3">
            <div className="h-1 rounded-full bg-primary-800 overflow-hidden">
              <div className="h-full w-1/3 bg-secondary-500" />
            </div>
            <div className="mt-2 flex items-center justify-between text-xs text-background-300">
              <span>00:00 / {video.duration}</span>
              <span>高清 HD</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 px-4 py-3 text-xs text-background-400">
          <span className="inline-flex items-center gap-1">
            <i className="ri-information-line" />
            仅前端演示，不提供真实视频播放与下载
          </span>
        </div>
      </div>
    </div>
  );
}